import React, { useCallback, useMemo, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Loader2, QrCode, Tv, RefreshCw, Copy, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

/**
 * ShareQRSection — dual-purpose QR block for the landing page.
 *
 * If the visitor is logged in ("device pairing" mode):
 *   • Button generates a one-time device-link token (POST /api/auth/device-link/create)
 *   • QR encodes {origin}/device?token=... — scanning it on a Smart TV / phone
 *     signs the second device in as the same user. Token expires in 5 min.
 *
 * If the visitor is anonymous ("intro share" mode):
 *   • QR encodes the landing origin so anyone can point their camera at it
 *     and land on the signup page.
 */
export default function ShareQRSection() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [deviceLink, setDeviceLink] = useState(null); // {url, expiresAt}

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const generateDeviceLink = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/device-link/create`, {});
      const url = `${origin}/device?token=${encodeURIComponent(r.data.token)}`;
      setDeviceLink({ url, expiresAt: r.data.expires_at });
      toast.success("New pairing code ready — valid for 5 min.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create pairing code");
    } finally { setBusy(false); }
  }, [user, origin]);

  const activeUrl = user ? (deviceLink?.url || null) : origin;
  const isPairing = !!user;

  const copyUrl = () => {
    if (!activeUrl) return;
    navigator.clipboard.writeText(activeUrl);
    toast.success("Link copied.");
  };

  const shareNative = async () => {
    if (!activeUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: isPairing ? "Open NeuralDeck on this device" : "NeuralDeck AI DJ",
          text: isPairing
            ? "Scan or open this to sign in on your Smart TV / second device."
            : "AI-powered 4-deck DJ studio — mix in the browser.",
          url: activeUrl,
        });
      } catch { /* cancelled */ }
    } else {
      copyUrl();
    }
  };

  const remainingSec = useMemo(() => {
    if (!deviceLink?.expiresAt) return null;
    return Math.max(0, Math.round((new Date(deviceLink.expiresAt).getTime() - Date.now()) / 1000));
  }, [deviceLink]);

  return (
    <section id="qr-share" className="px-6 py-20 border-t border-[#1E2638] max-w-7xl mx-auto" data-testid="landing-qr-section">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div className="space-y-4">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]">// {isPairing ? "Device pairing" : "Beam it to a friend"}</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight">
            {isPairing ? (
              <>Send your session to a <span className="text-[#00F0FF]">Smart TV</span> in one scan.</>
            ) : (
              <>Scan to <span className="text-[#00F0FF]">try it</span> — anywhere, no download.</>
            )}
          </h2>
          <p className="text-[#94A3B8] text-base max-w-lg">
            {isPairing
              ? "Generate a one-time QR code, point your TV / phone camera at it, and you're signed into the same NeuralDeck session — no re-typing passwords, no cables. Codes expire after 5 minutes for safety."
              : "Point your phone camera at the code to open NeuralDeck instantly. Share it with a friend who's about to hear an AI mix for the first time."}
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            {isPairing ? (
              <Button data-testid="qr-generate-button" onClick={generateDeviceLink} disabled={busy}
                className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : deviceLink ? <RefreshCw className="w-4 h-4 mr-2"/> : <Tv className="w-4 h-4 mr-2"/>}
                {deviceLink ? "New Code" : "Pair a Device"}
              </Button>
            ) : null}
            {activeUrl && (
              <>
                <Button data-testid="qr-copy-link" onClick={copyUrl} variant="outline"
                  className="border-[#2A354B] text-white hover:bg-white/5 font-black uppercase tracking-widest">
                  <Copy className="w-4 h-4 mr-2"/> Copy Link
                </Button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <Button data-testid="qr-share-native" onClick={shareNative} variant="outline"
                    className="border-[#2A354B] text-white hover:bg-white/5 font-black uppercase tracking-widest">
                    <Share2 className="w-4 h-4 mr-2"/> Share
                  </Button>
                )}
              </>
            )}
          </div>
          {isPairing && deviceLink && remainingSec != null && (
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#94A3B8]" data-testid="qr-expiry">
              {remainingSec > 0 ? `Expires in ~${Math.ceil(remainingSec / 60)} min` : "Expired — tap New Code"}
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <div className="relative p-4 rounded-2xl bg-white shadow-[0_0_60px_rgba(0,240,255,0.15)]" data-testid="qr-canvas-wrap">
            {activeUrl ? (
              <QRCodeSVG
                value={activeUrl}
                size={240}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#0B0E14"
                data-testid="landing-qr-code"
              />
            ) : (
              <div className="w-[240px] h-[240px] flex flex-col items-center justify-center text-center px-4 gap-2 text-[#0B0E14]" data-testid="qr-placeholder">
                <QrCode className="w-12 h-12 opacity-60"/>
                <div className="text-xs font-mono uppercase tracking-widest">Tap "Pair a Device" to generate</div>
              </div>
            )}
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#0B0E14] border border-[#00F0FF]/50 text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">
              {isPairing ? "Auto-Login QR" : "Share QR"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
